import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper, Divider, Chip, Avatar } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getUser } from '../../services/userService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import { User } from '../../types/user';
import { getRelativeFileUrl } from '../../services/relativeFilesService';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * Função para buscar os detalhes do usuário.
   */
  const fetchUserData = async () => {
    setLoading(true);
    try {
      if (userId) {
        const data = await getUser(userId);
        setUserData(data);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do usuário:', error);
      swal('Erro', 'Não foi possível buscar os detalhes do usuário.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Typography variant="h6" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Usuário não encontrado.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Cabeçalho com botão de voltar e editar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)} // Volta para a página anterior
          sx={{ textTransform: 'none' }}
        >
          Voltar
        </Button>
        <Typography variant="h4">Detalhes do Usuário</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/usuarios/${userData.id}/editar`)}
        >
          Editar Usuário
        </Button>
      </Box>

      {/* Informações Básicas */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Avatar
              alt={userData.full_name}
              src={`${getRelativeFileUrl(userData.profile_picture)}`}
              sx={{ width: 64, height: 64, mb: 2 }}
            />
            <Typography variant="h6">Nome Completo:</Typography>
            <Typography variant="body1">{userData.full_name}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Email:</Typography>
            <Typography variant="body1">{userData.email}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Telefone:</Typography>
            <Typography variant="body1">{userData.phone_number}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">CPF:</Typography>
            <Typography variant="body1">{userData.cpf || 'Não informado'}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Ativo:</Typography>
            <Typography variant="body1">{userData.is_active ? 'Sim' : 'Não'}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Administrador:</Typography>
            <Typography variant="body1">{userData.is_system_admin ? 'Sim' : 'Não'}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Sistemas:</Typography>
            {userData.systems.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {userData.systems.map((system) => (
                  <Chip key={system.id} label={system.name} color="primary" />
                ))}
              </Box>
            ) : (
              <Typography variant="body1">Nenhum sistema associado.</Typography>
            )}
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Entidades Públicas:</Typography>
            {userData.entities.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {userData.entities.map((entity) => (
                  <Chip key={entity.id} label={entity.name} color="secondary" />
                ))}
              </Box>
            ) : (
              <Typography variant="body1">Nenhuma entidade pública associada.</Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Informações de Login */}
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6">Último Login:</Typography>
            <Typography variant="body1">
              {userData.last_login ? new Date(userData.last_login).toLocaleString() : '—'}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Criado em:</Typography>
            <Typography variant="body1">
              {new Date(userData.created_at).toLocaleString()}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6">Atualizado em:</Typography>
            <Typography variant="body1">
              {new Date(userData.updated_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserDetail;